import React, { useEffect, useState } from "react";

/**
 * v8 — CLASSIC ENERGETIC splash (3800 ms total).
 *   Static TA + rotating stellar ring + shockwaves + orbiting particles. NO sunlight rays.
 *
 * Phase 1 (0-400)     Pure black void
 * Phase 2 (400-1000)  Laser sweep paints the "TA" (clip-path reveal)
 * Phase 3 (1000-1900) Chrome shimmer + rotating ring + orbiting particles
 * Phase 4 (1900-2400) Ring releases outward + 3 shockwave rings expand
 * Phase 5 (2400-3200) "THERMAL ACADEMY" brand fades in and holds
 * Phase 6 (3200-3800) Full-screen fade-out → onDone
 */
export default function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState(1);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(2),  400);
    const t2 = setTimeout(() => setPhase(3), 1000);
    const t3 = setTimeout(() => setPhase(4), 1900);
    const t4 = setTimeout(() => setPhase(5), 2400);
    const t5 = setTimeout(() => setFading(true), 3200);
    const t6 = setTimeout(() => onDone && onDone(), 3800);
    return () => [t1, t2, t3, t4, t5, t6].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div
      data-testid="splash-screen"
      data-phase={phase}
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "#000000",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        opacity: fading ? 0 : 1,
        transition: "opacity 800ms cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: fading ? "none" : "auto",
      }}>

      <div style={{ position: "relative", width: 0, height: 0 }}>

        {/* PHASE 2 — horizontal laser sweep that reveals TA */}
        {phase === 2 && (
          <div className="splash6-laser" data-testid="splash-laser"/>
        )}

        {/* PHASES 2-5 — "TA" monogram (left-to-right clip-path reveal in phase 2,
            chrome shimmer activates from phase 3 onward) */}
        {phase >= 2 && (
          <div className={`splash6-ta-wrap splash6-ta-p${phase}`}>
            <span className="splash6-ta">TA</span>
          </div>
        )}

        {/* PHASES 3-4 — ROTATING STELLAR RING around the static TA (releases at phase 4) */}
        {phase >= 3 && phase <= 4 && (
          <>
            <div className={`splash6-ring splash6-ring-p${phase}`} data-testid="splash-ring"/>
            <div className={`splash6-ring-glow splash6-ring-glow-p${phase}`}/>
          </>
        )}

        {/* PHASE 4-5 — 3 staggered shockwave rings */}
        {phase >= 4 && phase <= 5 && (
          <>
            <div className="splash6-shock" style={{ animationDelay: "0ms"   }}/>
            <div className="splash6-shock" style={{ animationDelay: "80ms"  }}/>
            <div className="splash6-shock" style={{ animationDelay: "160ms" }}/>
          </>
        )}

        {/* PHASE 3-5 — 6 orbiting micro-particles for energetic feel */}
        {phase >= 3 && phase <= 5 && (
          <>
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
              <div key={i} className="splash6-orbiter" style={{ animationDelay: `${i * 80}ms` }}>
                <span style={{ transform: `rotate(${deg}deg) translate(90px)` }} className="splash6-orbiter-dot"/>
              </div>
            ))}
          </>
        )}

        {/* PHASE 5 — brand text */}
        {phase === 5 && (
          <div className="splash6-brand">
            <div className="splash6-brand-title">THERMAL ACADEMY</div>
            <div className="splash6-brand-sub">INITIALIZING · v8</div>
          </div>
        )}
      </div>

      <style>{`
        /* TA monogram — STATIC (no rotation, centered) — the halo spins around it */
        .splash6-ta-wrap {
          position: absolute; left: 0; top: 0;
          display: flex; align-items: center; justify-content: center;
          transform: translate(-50%, -50%);
        }
        .splash6-ta {
          font-family: 'IBM Plex Sans', -apple-system, system-ui, sans-serif;
          font-size: 120px; font-weight: 900; line-height: 1;
          letter-spacing: -6px;
          background: linear-gradient(135deg,
            #e8e8e8 0%, #ffffff 25%, #a8a8a8 50%,
            #ffffff 75%, #e8e8e8 100%);
          background-size: 250% 250%;
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }

        /* PHASE 2 — laser-painted reveal via clip-path (left → right) */
        .splash6-ta-p2 .splash6-ta {
          animation: splash6TaReveal 800ms cubic-bezier(.7,0,.3,1) forwards;
        }
        @keyframes splash6TaReveal {
          0%   { clip-path: inset(0 100% 0 0); opacity: 1; filter: drop-shadow(0 0 12px rgba(34,211,238,0.9)); }
          100% { clip-path: inset(0 0    0 0); opacity: 1; filter: drop-shadow(0 0 0    rgba(34,211,238,0)); }
        }

        /* PHASES 3+ — chrome shimmer + breathe */
        .splash6-ta-p3 .splash6-ta,
        .splash6-ta-p4 .splash6-ta,
        .splash6-ta-p5 .splash6-ta {
          animation: splash6Shimmer 1.4s ease-in-out infinite,
                     splash6Breathe 1.2s ease-in-out infinite;
        }
        @keyframes splash6Shimmer {
          0%   { background-position: -150% -150%; }
          100% { background-position:  150%  150%; }
        }
        @keyframes splash6Breathe {
          0%, 100% { filter: drop-shadow(0 0 18px rgba(8,145,178,0.85))
                             drop-shadow(0 0 36px rgba(8,145,178,0.5)); }
          50%      { filter: drop-shadow(0 0 28px rgba(34,211,238,1))
                             drop-shadow(0 0 60px rgba(8,145,178,0.7))
                             drop-shadow(0 0 100px rgba(8,145,178,0.35)); }
        }

        /* PHASE 2 — horizontal laser scan sweeping across the screen */
        .splash6-laser {
          position: fixed; top: 50%; left: -120vw;
          width: 120vw; height: 3px;
          margin-top: -1px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(34,211,238,0.4) 30%,
            #FFFFFF 50%,
            rgba(34,211,238,0.4) 70%,
            transparent 100%);
          box-shadow: 0 0 20px #22D3EE, 0 0 40px rgba(8,145,178,0.6);
          animation: splash6Laser 800ms cubic-bezier(.7,0,.3,1) forwards;
        }
        @keyframes splash6Laser {
          0%   { left: -120vw; }
          100% { left: 100vw; }
        }

        /* PHASE 4 — shockwave rings */
        .splash6-shock {
          position: absolute; left: -80px; top: -80px;
          width: 160px; height: 160px; border-radius: 50%;
          border: 2px solid rgba(8,145,178,0.85);
          box-shadow: 0 0 24px rgba(34,211,238,0.5);
          animation: splash6Shock 600ms cubic-bezier(.2,.7,.3,1) forwards;
        }
        @keyframes splash6Shock {
          0%   { transform: scale(0.4); opacity: 0.85;
                 border-color: rgba(34,211,238,0.85); }
          100% { transform: scale(5); opacity: 0;
                 border-color: rgba(34,211,238,0); }
        }

        /* PHASE 5 — brand text + orbiters */
        .splash6-brand {
          position: absolute; top: 100px; left: 50%;
          transform: translateX(-50%);
          text-align: center; white-space: nowrap;
          opacity: 0;
          animation: splash6FadeUp 280ms ease-out forwards;
        }
        @keyframes splash6FadeUp {
          from { opacity: 0; transform: translate(-50%, 12px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        .splash6-brand-title {
          font-family: 'IBM Plex Sans', -apple-system, system-ui, sans-serif;
          font-size: 1.3rem; font-weight: 700;
          letter-spacing: 6px; text-transform: uppercase;
          background: linear-gradient(135deg, #fff 0%, #c8cfd4 50%, #fff 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
          text-shadow: 0 0 12px rgba(34,211,238,0.4);
        }
        .splash6-brand-sub {
          margin-top: 6px;
          color: rgba(255,255,255,0.5);
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 0.68rem; letter-spacing: 3px;
        }

        .splash6-orbiter {
          position: absolute; left: 0; top: 0; width: 0; height: 0;
          animation: splash6Orbit 2s linear infinite;
        }
        .splash6-orbiter-dot {
          display: block; width: 4px; height: 4px; border-radius: 50%;
          background: #22D3EE;
          box-shadow: 0 0 8px #22D3EE;
        }
        @keyframes splash6Orbit {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* ──────────── STELLAR CORE RING ──────────── */
        .splash6-ring-glow {
          position: absolute; left: 0; top: 0;
          width: 280px; height: 280px; margin: -140px 0 0 -140px;
          border-radius: 50%;
          background: radial-gradient(circle,
            rgba(34,211,238,0.18) 0%,
            rgba(8,145,178,0.10) 40%,
            transparent 70%);
          animation: splash6RingBreathe 1.5s ease-in-out infinite;
          pointer-events: none;
        }
        .splash6-ring {
          position: absolute; left: 0; top: 0;
          width: 280px; height: 280px; margin: -140px 0 0 -140px;
          border-radius: 50%;
          background:
            conic-gradient(from 0deg,
              #0891B2 0%, #22D3EE 18%, #ffffff 25%, #22D3EE 32%,
              #0891B2 50%, #22D3EE 68%, #ffffff 75%, #22D3EE 82%,
              #0891B2 100%);
          -webkit-mask:
            radial-gradient(circle, transparent 134px, #000 137px);
                  mask:
            radial-gradient(circle, transparent 134px, #000 137px);
          box-shadow:
            0 0 18px rgba(8,145,178,0.85),
            0 0 36px rgba(8,145,178,0.55),
            0 0 70px rgba(8,145,178,0.30),
            0 0 120px rgba(8,145,178,0.15),
            inset 0 0 18px rgba(34,211,238,0.45);
          animation:
            splash6RingAppear 240ms cubic-bezier(.34,1.56,.64,1) forwards,
            splash6RingShimmer 3.2s linear infinite,
            splash6RingBreathe 1.5s ease-in-out infinite;
        }
        @keyframes splash6RingAppear {
          0%   { transform: scale(0.0); opacity: 0; }
          70%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1.0); opacity: 1; }
        }
        @keyframes splash6RingShimmer {
          0%   { transform: scale(1.0) rotate(0deg); }
          100% { transform: scale(1.0) rotate(360deg); }
        }
        @keyframes splash6RingBreathe {
          0%, 100% { opacity: 0.92; }
          50%      { opacity: 1; }
        }
        /* RELEASE — ring expands outward and dissolves in phase 4 */
        .splash6-ring-p4 {
          animation:
            splash6RingRelease 300ms cubic-bezier(.2,.7,.3,1) forwards !important;
        }
        .splash6-ring-glow-p4 {
          animation:
            splash6RingGlowRelease 300ms ease-out forwards !important;
        }
        @keyframes splash6RingRelease {
          0%   { transform: scale(1.0); opacity: 1; }
          60%  { transform: scale(2.4); opacity: 0.5; }
          100% { transform: scale(4.0); opacity: 0; }
        }
        @keyframes splash6RingGlowRelease {
          0%   { transform: scale(1); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }

        /* 8 radiating light rays around the ring */
        /* Halo — the container that spins the ray corona TOGETHER (matches TA speed) */
        .splash6-halo {
          position: absolute; left: 0; top: 0;
          width: 0; height: 0;
          transform: translate(-50%, -50%);
          animation: splash6HaloSpin 0.6s linear infinite;
          filter: drop-shadow(0 0 12px rgba(0, 229, 255, 0.55));
        }
        @keyframes splash6HaloSpin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .splash6-ray {
          position: absolute; left: 0; top: 0;
          width: 2px; height: 22px; margin: -11px 0 0 -1px;
          background: linear-gradient(to top,
            rgba(34,211,238,1.0) 0%,
            rgba(34,211,238,0.0) 100%);
          transform-origin: center 70px;
          opacity: 0;
          animation: splash6RayBreathe 1.5s ease-in-out 0.2s infinite;
        }
        @keyframes splash6RayBreathe {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1.0;  }
        }
        .splash6-ray-p4 {
          animation:
            splash6RayRelease 300ms ease-out forwards !important;
        }
        @keyframes splash6RayRelease {
          0%   { opacity: 1.0; height: 22px; }
          100% { opacity: 0;   height: 55px; }
        }
      `}</style>
    </div>
  );
}
